import type { HTMLAttributes } from 'react';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { DownloadIcon, PresentationIcon, TrashIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import { IconButton } from '../buttons';
import { Spinner } from '../spinner';
import { Tooltip } from '../tooltip';

import styles from './file-card.module.css';

export type FileCardProps = {
  fileName?: string;
  fileUrl?: string | null;
  numberOfSlides?: number;
  language?: string;
  thumbnailUrl?: string;
  horizontal?: boolean;
  spinner?: boolean;
  progressMessage?: string;
  disableDeletion?: boolean;
  onThumbnailClick?: () => void;
  onDownload?: (fileUrl: string, fileName: string) => void;
  onDelete?: () => void;
} & HTMLAttributes<HTMLElement>;

/**
 * @param {string} [fileName] The name of the file.
 * @default undefined
 * @param {string | null} [fileUrl] The url of the file.
 * @default undefined
 * @param {number} [numberOfSlides] The number of slides in the file.
 * @default undefined
 * @param {string} [language] The language of the file.
 * @default undefined
 * @param {string} [thumbnailUrl] The url of the preview thumbnail.
 * @default undefined
 * @param {boolean} [horizontal] Whether the card should be displayed horizontally.
 * @default false
 * @param {boolean} [spinner] Whether the card should display a spinner.
 * @default false
 * @param {string} [progressMessage] The message to display when the file is being processed.
 * @default undefined
 * @param {boolean} [disableDeletion] Whether deletion of a presentation is possible or not.
 * @default false
 * @param {() => void} [onThumbnailClick] The callback to be called when the thumbnail is clicked.
 * @default undefined
 * @param {(fileUrl: string, fileName: string) => void} [onDownload] A callback that is called when the download button is clicked.
 * @default undefined
 * @param {() => void} [onDelete] A callback that is called when the delete button is clicked.
 * @default undefined
 */
export function FileCard({
  fileName,
  fileUrl,
  numberOfSlides = 0,
  language,
  thumbnailUrl,
  horizontal = false,
  spinner = false,
  progressMessage,
  disableDeletion,
  onThumbnailClick,
  onDownload,
  onDelete,
  ...props
}: FileCardProps) {
  const { t } = useTranslation();

  const downloadFile = () => {
    if (fileUrl && fileName) onDownload?.(fileUrl, fileName);
  };

  return (
    <div
      className={classNames(styles.wrapper, horizontal ? styles.horizontal : styles.vertical)}
      data-testid="file-card"
      {...props}
    >
      <div
        className={classNames(styles.thumbnail, spinner && styles.hasSpinner)}
        style={thumbnailUrl ? { backgroundImage: `url(${thumbnailUrl})` } : undefined}
      >
        {spinner && <Spinner size={60} className={styles.thumbnailSpinner} />}

        <button data-testid="file-card-thumbnail-button" type="button" onClick={onThumbnailClick}>
          {t('fileCard.thumbnail.buttonLabel')}
        </button>
      </div>

      <header>
        <PresentationIcon />

        <div className={styles.nameContainer}>
          <span data-testid="file-card-filename" className={styles.fileName}>
            {fileName}
          </span>

          <div className={styles.slidesNumber}>
            {spinner && <Spinner size="0.8em" />}

            <span data-testid="file-card-slides-number">
              {progressMessage || t('fileCard.slides.count', { count: numberOfSlides })}
            </span>
          </div>
        </div>
      </header>

      <footer>
        {language}

        <div className={styles.buttonsSection}>
          <Tooltip label={t('fileCard.button.download.label')}>
            <IconButton
              rounded
              data-testid="file-card-download-button"
              aria-label={t('fileCard.button.download.label')}
              onClick={downloadFile}
              disabled={!(fileUrl || onDownload)}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip label={t('fileCard.button.delete.label')}>
            <IconButton
              rounded
              data-testid="file-card-delete-button"
              aria-label={t('fileCard.button.delete.label')}
              onClick={onDelete}
              disabled={disableDeletion}
            >
              <TrashIcon />
            </IconButton>
          </Tooltip>
        </div>
      </footer>
    </div>
  );
}
